import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Leaf, Wind, Moon, ShieldCheck } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import PageHead from "@/components/site/PageHead";
import usePageContent from "@/hooks/usePageContent";
import { useLanguage, withLocalePrefix } from "@/context/LanguageContext";

const ease = [0.16, 1, 0.3, 1];

const COLLECTIONS = [
    {
        id: "m1",
        name: "Signature Ferme",
        desc: "Maintien optimal de la colonne, soutien ferme pour les dormeurs sur le dos et le ventre.",
        image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
        tags: ["Ferme", "Latex naturel", "Housse coton bio"],
    },
    {
        id: "m2",
        name: "Équilibre",
        desc: "Confort polyvalent, soutien équilibré pensé pour tous les types de morphologies.",
        image: "https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
        tags: ["Médium", "Mousse mémoire", "7 zones de confort"],
    },
    {
        id: "m3",
        name: "Nuage Moelleux",
        desc: "Accueil enveloppant, idéal pour les dormeurs latéraux en quête de douceur.",
        image: "https://images.unsplash.com/photo-1540518614846-7eded433c457?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
        tags: ["Moelleux", "Plumes & latex", "Surmatelas intégré"],
    },
];

const BENEFITS = [
    { icon: Leaf, title: "Matières naturelles", text: "Coton bio, latex naturel et laine vierge sélectionnés pour leur durabilité." },
    { icon: Wind, title: "Respirabilité totale", text: "Une thermorégulation pensée pour un sommeil frais toute l'année." },
    { icon: Moon, title: "Sommeil profond", text: "Un soutien anatomique millimétré qui suit chaque courbe du corps." },
    { icon: ShieldCheck, title: "Garantie 10 ans", text: "Chaque matelas est garanti, fabriqué en Belgique avec un savoir-faire artisanal." },
];

export default function Matelas() {
    const t = usePageContent("matelas");
    const { locale } = useLanguage();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main data-testid="matelas-page" className="min-h-screen bg-[#FDFBF7] text-[#1E3A5F]">
            <PageHead seo={t.seo} path="/matelas" />
            <Navbar />

            {/* HERO */}
            <section className="relative min-h-[80vh] flex items-end overflow-hidden pt-32">
                <motion.img
                    src={t("hero.image", "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85")}
                    alt="Matelas haut de gamme"
                    initial={{ scale: 1.06, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.6, ease }}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A5F]/45 via-[#1E3A5F]/30 to-[#1E3A5F]/70" />
                <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />

                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.3, ease }}
                        className="flex items-center gap-3 mb-8"
                    >
                        <span className="h-px w-12 bg-white/70" />
                        <span className="overline !text-white">{t("hero.overline", "Collection · Matelas")}</span>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 28 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, delay: 0.45, ease }}
                        className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] tracking-tight max-w-5xl"
                    >
                        {t("hero.title", "L'art du sommeil,")}<br />
                        <span className="italic font-normal text-white/90">{t("hero.title_italic", "pensé pour durer.")}</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.75, ease }}
                        className="mt-8 text-white/85 text-lg md:text-xl max-w-2xl font-light leading-relaxed"
                    >
                        {t(
                            "hero.subtitle",
                            "Une sélection de matelas haut de gamme, fabriqués avec des matières naturelles et un soutien millimétré pour chaque morphologie.",
                        )}
                    </motion.p>
                </div>
            </section>

            {/* COLLECTIONS */}
            <section className="bg-[#FDFBF7] py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-20">
                        <div className="md:col-span-6">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="h-px w-10 bg-[#DC2626]" />
                                <span className="overline">01 · Collections</span>
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                                Trois conforts,<br />
                                <span className="italic">une exigence.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5 md:col-start-8 flex items-end">
                            <p className="text-[#64748B] text-lg leading-relaxed">
                                Chaque matelas est conçu en collaboration avec nos artisans tapissiers
                                pour offrir un soutien anatomique précis et une longévité exceptionnelle.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {COLLECTIONS.map((c, i) => (
                            <motion.article
                                key={c.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.9, delay: i * 0.1, ease }}
                                data-testid={`matelas-card-${i}`}
                                className="group"
                            >
                                <div className="img-zoom aspect-[4/5] bg-[#F4F2EE]">
                                    <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover" />
                                </div>
                                <div className="mt-6">
                                    <h3 className="font-serif text-2xl md:text-3xl text-[#1E3A5F]">{c.name}</h3>
                                    <p className="mt-3 text-[#64748B] leading-relaxed">{c.desc}</p>
                                    <ul className="mt-5 flex flex-wrap gap-2">
                                        {c.tags.map((t) => (
                                            <li key={t} className="text-xs uppercase tracking-[0.18em] px-3 py-2 border border-[#1E3A5F]/20 text-[#1E3A5F]">
                                                {t}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="bg-[#F4F2EE] py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
                        <div className="md:col-span-6">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="h-px w-10 bg-[#DC2626]" />
                                <span className="overline">{t("benefits.overline", "02 · Notre exigence")}</span>
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                                {t("benefits.title", "Le sommeil")}<br />
                                <span className="italic">{t("benefits.title_italic", "comme un rituel.")}</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5 md:col-start-8 flex items-end">
                            <p className="text-[#64748B] text-lg leading-relaxed">
                                {t(
                                    "benefits.description",
                                    "Nous sélectionnons chaque composant avec rigueur — pour offrir un matelas qui vieillit avec vous, sans jamais perdre son maintien.",
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14">
                        {BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <motion.div
                                    key={b.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.8, delay: i * 0.08, ease }}
                                    data-testid={`matelas-benefit-${i}`}
                                >
                                    <div className="w-12 h-12 flex items-center justify-center border border-[#1E3A5F]/25 text-[#1E3A5F] mb-6">
                                        <Icon size={20} strokeWidth={1.4} />
                                    </div>
                                    <h3 className="font-serif text-2xl text-[#1E3A5F]">{b.title}</h3>
                                    <p className="mt-3 text-[#64748B] leading-relaxed">{b.text}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* GUIDE */}
            <section className="bg-[#1E3A5F] text-white py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#DC2626]" />
                            <span className="overline text-white/70">03 · Notre conseil</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            Bien choisir<br />
                            <span className="italic">son matelas.</span>
                        </h2>
                        <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-md">
                            Nos conseillers vous reçoivent en showroom à Etterbeek pour
                            tester nos modèles et trouver le couchage parfait pour votre sommeil.
                        </p>
                    </div>

                    <div className="md:col-span-5 md:col-start-8">
                        <ul className="space-y-5">
                            {[
                                "Diagnostic morphologique personnalisé",
                                "Essai libre en showroom",
                                "Livraison & installation à Bruxelles",
                                "Reprise de votre ancien matelas",
                                "Garantie 10 ans sur tous les modèles",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-4 border-b border-white/15 pb-5">
                                    <Check size={18} strokeWidth={1.5} className="mt-1 text-[#DC2626] shrink-0" />
                                    <span className="text-lg">{item}</span>
                                </li>
                            ))}
                        </ul>

                        <Link
                            to={`${withLocalePrefix("/", locale)}#contact`}
                            data-testid="matelas-cta-contact"
                            className="btn-accent mt-12 group"
                        >
                            Prendre rendez-vous
                            <ArrowRight size={18} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
